import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface DayHours {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

export interface BusinessSettings {
  id: string;
  business_name: string;
  business_email: string;
  business_phone: string;
  hours_of_operation: DayHours[];
  address: string;
  city: string;
  state: string;
  zip: string;
  created_at: string;
  updated_at: string;
}

const DEFAULT_NAME = 'Proven Realty Group';
const DEFAULT_PHONE = '(206) 555-1234';
const DEFAULT_EMAIL = 'hello@theprovenrealtygroup.com';

export function useBusinessSettings() {
  const [settings, setSettings] = useState<BusinessSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('business_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setSettings(data as BusinessSettings | null);
    } catch (err) {
      console.error('Failed to load business settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const businessName = settings?.business_name || DEFAULT_NAME;
  const businessPhone = settings?.business_phone || DEFAULT_PHONE;
  const businessEmail = settings?.business_email || DEFAULT_EMAIL;
  const businessHours = settings?.hours_of_operation || [];
  const businessAddress = settings
    ? [settings.address, settings.city, settings.state, settings.zip].filter(Boolean).join(', ')
    : '';

  return {
    settings,
    loading,
    businessName,
    businessPhone,
    businessEmail,
    businessHours,
    businessAddress,
    refetch: fetchSettings,
  };
}